const REGISTRATION_ERROR_CODES = ["expired_link", "unavailable", "setup_failed"] as const;

export type RegistrationErrorCode = (typeof REGISTRATION_ERROR_CODES)[number];

const SUPPORTED_CLOUDS = ["aws", "azure", "gcp"] as const;

type SupportedCloud = (typeof SUPPORTED_CLOUDS)[number];

const MARKETPLACE_NAMES: Record<SupportedCloud, string> = {
  aws: "AWS Marketplace",
  azure: "Microsoft Azure Marketplace",
  gcp: "Google Cloud Marketplace",
};

type NextStepArgs = { marketplace: string };

type ErrorCopy = {
  headline: string;
  nextStep: (args: NextStepArgs) => string;
};

const ERROR_COPY_BY_CODE: Record<RegistrationErrorCode, ErrorCopy> = {
  expired_link: {
    headline: "This registration link has expired or has already been used.",
    nextStep: ({ marketplace }) =>
      marketplace
        ? `Return to ${marketplace} and start the setup again.`
        : "Return to the marketplace and start the setup again.",
  },
  unavailable: {
    headline: "This registration page isn't available.",
    // The provider's listing carries their own support contact, which outlives their
    // account with us — unlike any channel we could point at here.
    nextStep: ({ marketplace }) =>
      marketplace
        ? `Contact the software provider using the support details on their ${marketplace} listing.`
        : "Contact the software provider using the support details on their marketplace listing.",
  },
  setup_failed: {
    headline: "We couldn't complete your registration.",
    nextStep: () =>
      "This may be temporary. Try again, and if it keeps happening contact the software provider.",
  },
};

// Guard before indexing, or "constructor" and "__proto__" resolve through Object.prototype
// into buyer-facing copy.
function isRegistrationErrorCode(value: string): value is RegistrationErrorCode {
  return (REGISTRATION_ERROR_CODES as readonly string[]).includes(value);
}

function isSupportedCloud(value: string): value is SupportedCloud {
  return (SUPPORTED_CLOUDS as readonly string[]).includes(value);
}

export function registrationErrorCopy(
  code: string,
  cloud: string,
): { code: RegistrationErrorCode; headline: string; nextStep: string } {
  const resolvedCode: RegistrationErrorCode = isRegistrationErrorCode(code) ? code : "setup_failed";
  const normalisedCloud = cloud.toLowerCase();
  const marketplace = isSupportedCloud(normalisedCloud) ? MARKETPLACE_NAMES[normalisedCloud] : "";
  const copyForCode = ERROR_COPY_BY_CODE[resolvedCode];

  return {
    code: resolvedCode,
    headline: copyForCode.headline,
    nextStep: copyForCode.nextStep({ marketplace }),
  };
}
